import 'dayjs/locale/en';
import 'dayjs/locale/fi';
import React from 'react';
import { useMessageGetter } from 'react-message-context';
import styled from 'styled-components/macro';
import { FileIcon } from '../../../assets';
import { formatDateTimeFromString } from '../../../utils';
import { Column, GridContainer, PreviewContentBlock, Row } from '../layoutComponents';
import { H2, H3, TextBlock } from '../Text';
import { GeneralExpenseReportFormState } from './schema';
import { getFileDisplayName } from '../form-utils';
import { SignedUrl, UploadedFileMetadata } from '../../../types/forms';

const PreviewContent = styled(PreviewContentBlock)`
  gap: 40px;
`;

const PurchaseReportsContainer = styled(PreviewContentBlock)``;

const PurchaseReportContainer = styled(GridContainer)`
  padding-bottom: 20px;
  gap: 20px;
  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  }
`;

const FileLink = styled.a`
  display: inline-flex;
  text-decoration: underline;
  color: black;
`;

const TotalAmount = styled.span`
  font-size: 24px;
  line-height: 36px;
  font-family: ${(props) => props.theme.text.secondaryFont};
`;

type Props = { formData: GeneralExpenseReportFormState; signedUrls: SignedUrl[] };

export const GeneralExpenseReportPreview = ({ formData, signedUrls }: Props): React.ReactElement => {
  const t = useMessageGetter('forms.generalExpenseReport');

  const formattedDate = formData.event.date ? formatDateTimeFromString(formData.event.date, true) : '';

  const totalExpensesAmount = formData.purchaseReports.reduce((sum, obj) => sum + obj.amount, 0).toFixed(2);

  const getFileHref = (file: File | UploadedFileMetadata): string | undefined => {
    if (file instanceof File) {
      return URL.createObjectURL(file);
    }
    return signedUrls.find(({ id }) => id === file.id)?.signedUrl;
  };

  return (
    <PreviewContent>
      <H2>{t('title')}</H2>
      <H3>{t('contactInfo.title')}</H3>
      <GridContainer rowGap="24px">
        <Column>
          <span>{t('contactInfo.name')}</span>
          <span>{formData.contactInfo.name}</span>
        </Column>
        <Column span={2}>
          <span>{t('contactInfo.address')}</span>
          <span>{formData.contactInfo.address}</span>
          <span>
            {formData.contactInfo.zip} {formData.contactInfo.city}
          </span>
        </Column>
        <Column>
          <span>{t('contactInfo.phone')}</span>
          <span>{formData.contactInfo.phone}</span>
        </Column>
        <Column>
          <span>{t('contactInfo.email')}</span>
          <span>{formData.contactInfo.email}</span>
        </Column>
        <Column span={2}>
          <span>{t('contactInfo.iban')}</span>
          <span>{formData.contactInfo.iban}</span>
        </Column>
      </GridContainer>
      <H3>{t('event.title')}</H3>
      <GridContainer rowGap="24px">
        <Column span={2}>
          <span>{t('event.name')}</span>
          <span>{formData.event.name}</span>
        </Column>
        <Column span={2}>
          <span>{t('event.additionalInformation')}</span>
          <TextBlock>{formData.event.additionalInformation}</TextBlock>
        </Column>
        <Column>
          <span>{t('event.date')}</span>
          <span>{formattedDate}</span>
        </Column>
      </GridContainer>
      <H3>{t('purchaseReports.title')}</H3>
      <PurchaseReportsContainer>
        {formData.purchaseReports.map((purchaseReport, index) => {
          return (
            <PurchaseReportContainer key={index} columns={5}>
              <Column span={4}>
                <span>
                  {t('purchaseReports.report')} {index + 1}
                </span>
                <span>{purchaseReport.report}</span>
              </Column>
              <Column>
                <span>{t('purchaseReports.amount')}</span>
                <span>{purchaseReport.amount.toFixed(2)}</span>
              </Column>
              {purchaseReport.receipts && (
                <Column span={5}>
                  <FileLink
                    href={getFileHref(purchaseReport.receipts[0])}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Row>
                      <FileIcon style={{ flexShrink: 0 }} />
                      <TextBlock>{getFileDisplayName(purchaseReport.receipts?.[0])}</TextBlock>
                    </Row>
                  </FileLink>
                </Column>
              )}
            </PurchaseReportContainer>
          );
        })}
      </PurchaseReportsContainer>
      <TotalAmount>{t('purchaseReports.total', { amount: totalExpensesAmount })}</TotalAmount>
    </PreviewContent>
  );
};
